import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// Copy of the main app's client/src/components/EditRequirementModal.js (plan 18.3). The raw
// import_metadata textarea below is the way to adjust a criterion's observable_items during
// the demo (18.3), since there is no dedicated observable-items editor.
const emptyForm = () => ({
  section: '',
  subsection: '',
  title: '',
  description: '',
  criteria: '',
  mandatory: true,
  weight: 1,
});

export default function EditRequirementModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  standardName,
  requirement,
}) {
  const [form, setForm] = useState(emptyForm);
  const [importMetaJson, setImportMetaJson] = useState('');
  const [showMetaSection, setShowMetaSection] = useState(false);
  const [errors, setErrors] = useState({});

  const resetForm = useCallback(() => {
    setForm(emptyForm());
    setImportMetaJson('');
    setShowMetaSection(false);
    setErrors({});
  }, []);

  useEffect(() => {
    if (!isOpen || !requirement) return;
    setForm({
      section: requirement.section ?? '',
      subsection: requirement.subsection ?? '',
      title: requirement.title ?? '',
      description: requirement.description ?? '',
      criteria: requirement.criteria ?? '',
      mandatory: Boolean(requirement.mandatory),
      weight: requirement.weight != null ? Number(requirement.weight) : 1,
    });
    const meta = requirement.import_metadata;
    setImportMetaJson(
      meta && typeof meta === 'object' && Object.keys(meta).length > 0
        ? JSON.stringify(meta, null, 2)
        : ''
    );
    setShowMetaSection(false);
    setErrors({});
  }, [isOpen, requirement]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    const next = {};
    if (!form.section.trim()) next.section = 'Section is required';
    if (!form.title.trim()) next.title = 'Title is required';
    if (!form.description.trim()) next.description = 'Description is required';
    if (!form.criteria.trim()) next.criteria = 'Criteria is required';
    if (Number.isNaN(form.weight) || form.weight < 0 || form.weight > 100) {
      next.weight = 'Weight must be between 0 and 100';
    }
    let parsedMeta = undefined;
    const trimmed = importMetaJson.trim();
    if (trimmed) {
      try {
        parsedMeta = JSON.parse(trimmed);
        if (parsedMeta === null || typeof parsedMeta !== 'object' || Array.isArray(parsedMeta)) {
          next.importMetaJson = 'Must be a JSON object (e.g. {"points": 2})';
        }
      } catch {
        next.importMetaJson = 'Invalid JSON';
      }
    }
    setErrors(next);
    return { ok: Object.keys(next).length === 0, parsedMeta };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { ok, parsedMeta } = validateForm();
    if (!ok) return;

    const payload = {
      section: form.section.trim(),
      subsection: form.subsection.trim() || null,
      title: form.title.trim(),
      description: form.description.trim(),
      criteria: form.criteria.trim(),
      mandatory: form.mandatory,
      weight: Number(form.weight),
    };
    if (parsedMeta !== undefined) {
      payload.import_metadata = parsedMeta;
    }

    try {
      await onSubmit(payload);
      resetForm();
    } catch {
      /* parent shows toast */
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (!isOpen || !requirement) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Edit requirement</h3>
            <p className="text-sm text-gray-500">
              Administrator only · {standardName}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isSubmitting}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section *</label>
                  <input
                    type="text"
                    value={form.section}
                    onChange={(e) => updateField('section', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.section ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                  />
                  {errors.section && <p className="text-red-500 text-xs mt-1">{errors.section}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subsection</label>
                  <input
                    type="text"
                    value={form.subsection}
                    onChange={(e) => updateField('subsection', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={form.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assessment criteria *</label>
                <textarea
                  value={form.criteria}
                  onChange={(e) => updateField('criteria', e.target.value)}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.criteria ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.criteria && <p className="text-red-500 text-xs mt-1">{errors.criteria}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="edit-mandatory"
                    checked={form.mandatory}
                    onChange={(e) => updateField('mandatory', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={isSubmitting}
                  />
                  <label htmlFor="edit-mandatory" className="ml-2 block text-sm text-gray-700">
                    Mandatory
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weight (0–100)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={form.weight}
                    onChange={(e) => updateField('weight', parseFloat(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.weight ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                  />
                  {errors.weight && <p className="text-red-500 text-xs mt-1">{errors.weight}</p>}
                </div>
              </div>

              <div className="border border-gray-200 rounded-md overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowMetaSection((s) => !s)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100"
                >
                  <span>Structured metadata (points, observable items, …)</span>
                  {showMetaSection ? (
                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                  )}
                </button>
                {showMetaSection && (
                  <div className="p-3 border-t border-gray-100 bg-white">
                    <p className="text-xs text-gray-500 mb-2">
                      Optional JSON merged into <code className="bg-gray-100 px-1 rounded">import_metadata</code>.
                      Examples: <code className="bg-gray-100 px-1 rounded">{`{"points": 3}`}</code>,{' '}
                      <code className="bg-gray-100 px-1 rounded">{`{"observable_items": [{"id": "x", "text": "..."}]}`}</code>.
                    </p>
                    <textarea
                      value={importMetaJson}
                      onChange={(e) => setImportMetaJson(e.target.value)}
                      rows={8}
                      placeholder='{ "points": 2, "observable_items": [] }'
                      className={`w-full px-3 py-2 border rounded-md font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.importMetaJson ? 'border-red-500' : 'border-gray-300'
                      }`}
                      disabled={isSubmitting}
                    />
                    {errors.importMetaJson && (
                      <p className="text-red-500 text-xs mt-1">{errors.importMetaJson}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-[#F8F8FF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
